import { ColorPicker, Input } from "antd";
import queryClient from "api/queryClient";
import { useUpdateFormMutation } from "api/TenantSlug/Form/hooks";
import { FormCol } from "components/UI/Form/FormCol";
import { FormGrid } from "components/UI/Form/FormGrid";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { LocationSectionHeader } from "components/UI/LocationSectionHeader";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { LabelSelectSearchable } from "components/UI/Selects/LabelSelectSearchable/LabelSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { FormId } from "types/models";

import { FormDesignGeneralFormData, schema } from "./formSchema";
import { FormSettingComponent } from "./FormSettingComponent";

import FormData = App.Data.FormData;
import StoreFormRequest = App.Http.Requests.Form.StoreFormRequest;

const transformFormData = (formData: FormData): FormDesignGeneralFormData => ({
    id: formData?.id,
    name: formData?.name,
    system_name: formData?.system_name,
    css: formData?.css,
    form_structure_id: formData?.form_structure_id || "",
    background_color: formData?.background_color ?? "#FFFFFF",
    width: formData?.width ?? "100%",
    primary_color: formData?.primary_color ?? "#000000",
    secondary_color: formData?.secondary_color ?? "#FFFFFF",
    border: formData?.border ?? "solid",
    border_color: formData?.border_color ?? "#000000",
    border_width: formData?.border_width ?? "1px",
    show_timer_on_seconds: formData?.show_timer_on_seconds,
    left_path_logo: formData?.left_path_logo,
    center_logo_path: formData?.center_logo_path,
    left_logo_id: formData?.left_logo_id,
    logo_file_id: formData?.logo_file_id,
    center_logo_id: formData?.center_logo_id,
    get_money_text: formData?.get_money_text,
    header_text: formData?.header_text,
    meta_title: formData?.meta_title,
    meta_description: formData?.meta_description,
    text_under_submit: formData?.text_under_submit,
    offers: formData?.offers?.map((offer) => offer.id) || [],
    category: formData?.category?.id ?? null,
    label_ids: formData?.labels?.map((label) => label.id) || [],
});

interface ContentProps {
    formData: FormData;
    onSubmit?: () => void;
}

const FormDesignGeneralFormContent: FC<ContentProps> = ({ formData }) => {
    const { t } = usePaldockTranslation("forms.form-design-general");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");

    const generalFormUtils = useGeneralForm<FormDesignGeneralFormData>();
    const { control, fieldNames, setValue, reset } = generalFormUtils;

    // Reset form when formData changes (after refetch)
    useEffect(() => {
        if (formData) {
            reset(transformFormData(formData));
        }
    }, [formData, reset]);

    return (
        <>
            <LocationSectionHeader />
            <FormCol>
                {/* Row 1: Form Structure, Name */}
                <FormGrid cols={2}>
                    <FormItem
                        control={control}
                        name={fieldNames.form_structure_id?.name || "form_structure_id"}
                        label={tFields("form_structure.label")}
                        required
                    >
                        <FormStructureSelectSearchable disabled={!!formData?.formPages?.length} />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.name?.name || "name"}
                        label={tFields("name.label")}
                        required
                    >
                        <Input placeholder={tFields("name.placeholder")} />
                    </FormItem>
                </FormGrid>
                {/* Row 2: Category, Tags */}
                <FormGrid cols={2}>
                    <FormItem
                        control={control}
                        name={fieldNames.category?.name || "category"}
                        label={tFields("category.label")}
                    >
                        <CategorySelectSearchable allowClear />
                    </FormItem>
                    <FormItem control={control} name="label_ids" label={tFields("label.label")}>
                        <LabelSelectSearchable mode="multiple" allowClear />
                    </FormItem>
                </FormGrid>
                {/* Row 2: All Colors */}
                <FormGrid cols={4}>
                    <FormItem
                        control={control}
                        name={fieldNames.background_color?.name || "background_color"}
                        label={t("fields.background_color.label")}
                    >
                        <ColorPicker size="small" showText allowClear />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.primary_color?.name || "primary_color"}
                        label={t("fields.primary_color.label")}
                    >
                        <ColorPicker size="small" showText allowClear />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.secondary_color?.name || "secondary_color"}
                        label={t("fields.secondary_color.label")}
                    >
                        <ColorPicker size="small" showText allowClear />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.border_color?.name || "border_color"}
                        label={t("fields.border_color.label")}
                    >
                        <ColorPicker size="small" showText allowClear />
                    </FormItem>
                </FormGrid>
                {/* Row 3: Border, Border Width */}
                <FormGrid cols={2}>
                    <FormItem
                        control={control}
                        name={fieldNames.border?.name || "border"}
                        label={t("fields.border.label")}
                    >
                        <Input placeholder={t("fields.border.placeholder")} />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.border_width?.name || "border_width"}
                        label={t("fields.border_width.label")}
                    >
                        <Input placeholder={t("fields.border_width.placeholder")} />
                    </FormItem>
                </FormGrid>
                {/* Row 4: Width, Left Logo ID */}
                <FormGrid cols={2}>
                    <FormItem
                        control={control}
                        name={fieldNames.width?.name || "width"}
                        label={t("fields.width.label")}
                    >
                        <Input placeholder={t("fields.width.placeholder")} />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.left_logo_id?.name || "left_logo_id"}
                        label={t("fields.left_logo_id.label")}
                    >
                        <Input placeholder={t("fields.left_logo_id.placeholder")} />
                    </FormItem>
                </FormGrid>
                {/* Row 4+5: Other settings */}
                {formData.id && <FormSettingComponent formData={formData} control={control} fieldNames={fieldNames} />}
                {/* Row 6: CSS */}
                <FormGrid cols={1}>
                    <FormItem control={control} name={fieldNames.css?.name || "css"} label={t("fields.css.label")}>
                        <Input.TextArea
                            rows={4}
                            placeholder={t("fields.css.placeholder")}
                            onChange={(e) => setValue("css", e.target.value)}
                        />
                    </FormItem>
                </FormGrid>
            </FormCol>
        </>
    );
};

interface FormDesignGeneralFormProps {
    formData: FormData;
    onSubmit?: () => void;
}

export const FormDesignGeneralForm: FC<FormDesignGeneralFormProps> = ({ formData, onSubmit }) => {
    const formId = FormId.FormDesignGeneralForm;
    const updateFormMutation = useUpdateFormMutation(formData?.id?.toString() || "");
    const { t } = usePaldockTranslation("forms.form-design-general");

    const defaultValues = transformFormData(formData);

    const onSubmitForm = (values: FormDesignGeneralFormData) => {
        updateFormMutation.mutate({ ...values, category_id: values.category ?? null } as StoreFormRequest, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["getForm", formData?.id?.toString()] });
                queryClient.invalidateQueries({ queryKey: ["getForms"] });
                toast.success(t("messages.update_success"));
                onSubmit?.();
            },
            onError: () => {
                toast.error(t("messages.update_error"));
            },
        });
    };

    return (
        <GeneralFormProvider<FormDesignGeneralFormData>
            schema={schema}
            defaultValues={defaultValues}
            id={formId}
            onSubmit={onSubmitForm}
            resetOnSubmit={false}
        >
            <FormDesignGeneralFormContent formData={formData} />
        </GeneralFormProvider>
    );
};
